import moment from 'moment';
import lodash from 'lodash';
import { createSelector } from 'reselect';
import { selectors as userSelectors } from '../users';
import { selectors as standbyDutyFunctionSelectors } from '../standby-duty-functions';
import { selectors as skillSelectors } from '../skills';
import { selectors as availabilityCodeSelectors } from '../availability-codes';
import { selectors as groupSelectors } from '../groups';

const findOrInitializeById = (byId, id) => id && (byId[id] || { id: Number(id) });

const dashboardDateTime = state => state.dashboards.dashboardDatetime.dateTime;

export const loading = state => state.combinedSchedules.full.loading;

export const selectMembershipCombinedScheduleState = state => state.combinedSchedules.full.membership;

const selectTeamCombinedScheduleState = state => state.combinedSchedules.full.team;

const selectTaskCombinedScheduleState = state => state.combinedSchedules.full.task;

export const selectLoading = state => state.combinedSchedules.full.loading;

export const selectScheduleWarnings = state => state.combinedSchedules.full.scheduleWarnings;

const computeMembershipStatusAt = (relevantState, users, skills, standbyDutyFunctions, codes, groups, time) => {
  const result = Object.values(relevantState)
    .map(schedule => {
      if (!schedule) return null;
      const interval = schedule.intervals.find(i =>
        // take the interval that applies right now. Start inclusive, end exclusive
        moment(time).isBetween(moment(i.start_time), moment(i.end_time), null, '[)')
      );

      // fitering by available has been moved to FilterObject
      if (!interval) return null;

      const user = findOrInitializeById(users, schedule.user_id);
      const userSkills = lodash.orderBy(
        interval.skill_ids.map(id => findOrInitializeById(skills, id)),
        ['order']
      );
      const assignedFunctions = interval.assigned_function_ids.map(id =>
        findOrInitializeById(standbyDutyFunctions, id)
      );
      const availabilityCode = findOrInitializeById(codes, interval.availability_code_id);
      const ownerId = schedule.owner_id;
      const groupId = schedule.group_id;
      const group = findOrInitializeById(groups, schedule.group_id);
      const displayOrder = schedule.display_order;
      const available = interval.available;

      return {
        ownerId,
        groupId,
        group,
        displayOrder,
        user,
        skills: userSkills,
        assignedFunctions,
        availabilityCode,
        available
      };
    })
    .filter(Boolean);
  return lodash.orderBy(result, ['displayOrder']);
};

export const transientSchedules = createSelector(selectMembershipCombinedScheduleState, membershipSchedules =>
  // Look for schedules that contain at least one transient interval
  Object.values(membershipSchedules).filter(schedule => schedule.intervals.find(interval => interval.transient))
);
export const error = state => state['combinedSchedules'].error;

export const unsavedChangesPresent = createSelector(transientSchedules, changes => changes.length > 0);

// returns members for the current time
export const membershipStatusNow = createSelector(
  selectMembershipCombinedScheduleState,
  userSelectors.byId,
  skillSelectors.byId,
  standbyDutyFunctionSelectors.byId,
  availabilityCodeSelectors.byId,
  groupSelectors.byId,
  (relevantState, users, skills, standbyDutyFunctions, codes, groups) => {
    return computeMembershipStatusAt(relevantState, users, skills, standbyDutyFunctions, codes, groups, moment());
  }
);

// returns members for the dashboard time
export const membershipStatusAtDashboardTime = createSelector(
  selectMembershipCombinedScheduleState,
  userSelectors.byId,
  skillSelectors.byId,
  standbyDutyFunctionSelectors.byId,
  availabilityCodeSelectors.byId,
  groupSelectors.byId,
  dashboardDateTime,
  (relevantState, users, skills, standbyDutyFunctions, codes, groups, dashboardTime) => {
    return computeMembershipStatusAt(relevantState, users, skills, standbyDutyFunctions, codes, groups, dashboardTime);
  }
);

// returns only available members fot the dashboard time, required for Crew Availability Widget
export const availableMembersAtDashboardTime = createSelector(membershipStatusAtDashboardTime, members => {
  return members.filter(member => member.available);
});

// returns the members for the whole period
export const allMembershipStatus = createSelector(
  selectMembershipCombinedScheduleState,
  userSelectors.byId,
  skillSelectors.byId,
  standbyDutyFunctionSelectors.byId,
  availabilityCodeSelectors.byId,
  groupSelectors.byId,
  (relevantState, users, skills, standbyDutyFunctions, codes, groups) => {
    const result = Object.values(relevantState).flatMap(schedule => {
      if (!schedule || !schedule.intervals) return null;
      return schedule.intervals
        .map(interval => {
          // fitering by available has been moved to FilterObject
          if (!interval) return null;

          const user = findOrInitializeById(users, schedule.user_id);
          const userSkills = lodash.orderBy(
            interval.skill_ids.map(id => findOrInitializeById(skills, id)),
            ['order']
          );
          const assignedFunctions = interval.assigned_function_ids.map(id =>
            findOrInitializeById(standbyDutyFunctions, id)
          );
          const availabilityCode = findOrInitializeById(codes, interval.availability_code_id);
          const ownerId = schedule.owner_id;
          const groupId = schedule.group_id;
          const group = findOrInitializeById(groups, schedule.group_id);
          const displayOrder = schedule.display_order;
          const startTime = interval.start_time;
          const endTime = interval.end_time;
          const available = interval.available;

          return {
            ownerId,
            groupId,
            group,
            displayOrder,
            user,
            skills: userSkills,
            assignedFunctions,
            availabilityCode,
            startTime,
            endTime,
            available
          };
        })
        .filter(Boolean);
    });
    return lodash.orderBy(result, ['displayOrder']);
  }
);

// helper function for createAvailabilitySelector and
// createDashboardTimeAvailabilitySelector
const activeIntervalsAt = (schedules, time) => {
  const result = {};
  for (const schedule of Object.values(schedules)) {
    if (!schedule) continue;
    const interval = schedule.intervals.find(i =>
      moment(time).isBetween(moment(i.start_time), moment(i.end_time), null, '[)')
    );

    if (!interval) continue;
    result[schedule.owner_id] = interval;
  }
  return result;
};

const createAvailabilitySelector = stateSelector =>
  createSelector(stateSelector, relevantState => {
    return activeIntervalsAt(relevantState, moment());
  });

const createDashboardTimeAvailabilitySelector = stateSelector =>
  createSelector(stateSelector, dashboardDateTime, (relevantState, dashboardDateTime) => {
    return activeIntervalsAt(relevantState, dashboardDateTime);
  });

// returns information about the availability of entities at current time
// Format: a hash from [entity_id] => { currentInterval }
export const currentTime = {
  team: createAvailabilitySelector(selectTeamCombinedScheduleState),
  task: createAvailabilitySelector(selectTaskCombinedScheduleState),
  membership: createAvailabilitySelector(selectMembershipCombinedScheduleState)
};

// returns information about the availability of entities at dashboard time
// Format: a hash from [entity_id] => { currentInterval }
export const dashboardTime = {
  team: createDashboardTimeAvailabilitySelector(selectTeamCombinedScheduleState),
  task: createDashboardTimeAvailabilitySelector(selectTaskCombinedScheduleState),
  membership: createDashboardTimeAvailabilitySelector(selectMembershipCombinedScheduleState)
};
